import { useEffect, useState } from 'react'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { useToast } from '@/components/ui/Toast'
import type { MentorCreated } from '@/types/api'
import styles from './CredentialDialog.module.css'
import { copyText } from '@/lib/clipboard'

interface Props {
  credential: MentorCreated | null
  onClose: () => void
}

/**
 * Shows a mentor's temporary password. **Once.**
 *
 * *** THIS IS THE ONE DIALOG THAT MUST NOT BE DISMISSIBLE BY ACCIDENT ***
 * The server stores only a hash, so this password cannot be retrieved. If the
 * admin closes this by pressing Escape or clicking the backdrop, the only
 * remedy is resetting the password again — which invalidates the one already
 * emailed to the mentor.
 *
 * So: no Escape, no backdrop dismissal, and the close button requires an
 * explicit acknowledgement that the password has been saved.
 */
export function CredentialDialog({ credential, onClose }: Props) {
  const toast = useToast()
  const [acknowledged, setAcknowledged] = useState(false)
  const [copied, setCopied] = useState(false)

  // Reset for each new credential — otherwise a second mentor's dialog opens
  // already acknowledged.
  useEffect(() => {
    if (credential) {
      setAcknowledged(false)
      setCopied(false)
    }
  }, [credential])

  const copy = async (text: string, label: string) => {
    // copyText falls back to execCommand, which is what actually works here:
    // this portal is served over plain HTTP, where navigator.clipboard does
    // not exist at all.
    if (await copyText(text)) {
      setCopied(true)
      toast.success(`${label} copied.`)
      return
    }
    // The value is selectable on screen, so say so rather than leaving the
    // click appearing to do nothing.
    toast.error('Could not copy. Select the text and copy it manually.')
  }

  if (!credential) return null

  const { mentor, temporary_password: password } = credential

  const both = `Email: ${mentor.email}\nTemporary password: ${password}`

  return (
    <Modal
      open
      onClose={onClose}
      title="Mentor account created"
      description={`${mentor.full_name} can now sign in.`}
      // The whole point: this cannot be dismissed accidentally.
      dismissible={false}
      size="md"
      /*
       * *** THE ACTION BELONGS IN THE STICKY FOOTER ***
       * It used to sit at the end of the scrolling body, so on a laptop screen
       * the dialog ran past the bottom of the window and the primary button was
       * cut off — the one dialog where the user must take an action before
       * closing. The Modal's footer stays put while the body scrolls.
       */
      footer={
        <div className={styles.footer}>
          <label className={styles.acknowledge}>
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(event) => setAcknowledged(event.target.checked)}
            />
            <span>I have saved this password or sent it to the mentor.</span>
          </label>
          <Button
            variant="primary"
            disabled={!acknowledged}
            onClick={onClose}
          >
            {acknowledged ? 'Done' : 'Save the password first'}
          </Button>
        </div>
      }
    >
      <div className={styles.body}>
        {/*
          The PASSWORD leads, not the email. It is the only value on this screen
          that cannot be recovered — the email is visible on the mentor list a
          second later. Ordering by importance, not by form convention.
        */}
        <div className={styles.passwordBlock}>
          <span className={styles.fieldLabel}>Temporary password</span>
          <div className={styles.fieldRow}>
            <input
              readOnly
              value={password}
              className={styles.passwordValue}
              onFocus={(event) => event.target.select()}
              aria-label="Temporary password"
            />
            <Button
              size="sm"
              variant="secondary"
              onClick={() => copy(password, 'Password')}
            >
              Copy
            </Button>
          </div>
          <p className={styles.warning}>
            Shown once. It is not stored anywhere we can read — if you lose it,
            the only remedy is another reset.
          </p>
        </div>

        <Field
          label="Email"
          value={mentor.email}
          onCopy={() => copy(mentor.email, 'Email')}
        />

        <button
          type="button"
          className={styles.copyBoth}
          onClick={() => copy(both, 'Both details')}
        >
          Copy both
        </button>

        <ol className={styles.steps}>
          <li>They sign in with these details.</li>
          <li>They must change the password immediately.</li>
          <li>
            They add a title and bio — until then,{' '}
            <strong>students cannot see or book them</strong>.
          </li>
        </ol>

        {copied && !acknowledged && (
          <p className={styles.copiedHint}>Copied. Tick the box below to close.</p>
        )}
      </div>
    </Modal>
  )
}

function Field({
  label,
  value,
  onCopy,
}: {
  label: string
  value: string
  onCopy: () => void
}) {
  return (
    <div className={styles.field}>
      <span className={styles.fieldLabel}>{label}</span>
      <div className={styles.fieldRow}>
        {/*
          A readonly input rather than a <span>: it is selectable with a
          keyboard, works with select-all, and survives a failed clipboard API.
        */}
        <input
          readOnly
          value={value}
          className={styles.fieldValue}
          onFocus={(event) => event.target.select()}
          aria-label={label}
        />
        <Button size="sm" variant="secondary" onClick={onCopy}>
          Copy
        </Button>
      </div>
    </div>
  )
}
